"use client"

import React, { useEffect, useState } from "react"
import Link from "next/link"
import { SEARCH_PARAMS_PAGE_VIEW_OPTIONS } from "@/constants/ui"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { ProductPriceContainerCtnrROV1 } from "@ub/pricing-client"
import { useTranslations } from "next-intl"
import { ulid } from "ulid"

import { createDiscountDisplayOptions, determineCardPrices } from "@/lib/price-utils"
import { ReducedProductData } from "@/lib/storefront/product/product"
import { determinePrefixedProductId, showProductPrice } from "@/lib/storefront/product/productHelper"
import { storeFactFinderSessionAttribution } from "@/lib/tracking/factfinder-session-attribution"
import {
  buildCategoryListingAttribution,
  clearListingAttribution,
  storeListingAttribution,
} from "@/lib/tracking/listing-attribution"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/components"
import AddToBasketButtonClient from "@/components/product/AddToBasketButtonClient"
import FFProductClickTrackingClient from "@/components/product/FFProductClickTrackingClient"
import FFRecommendationClickTrackingClient from "@/components/product/FFRecommendationClickTrackingClient"
import { AddToBaskedWithQuantityInputClient } from "@/components/product/pdp/buybox/AddToBasketWithQuantityInputClient"
import { ProductCardDeliveryInfo } from "@/components/product/ProductCardDeliveryInfo"
import ProductCardDiscount from "@/components/product/ProductCardDiscount"
import ProductCardPrice from "@/components/product/ProductCardPrice"
import ImageWithFallback from "@/components/storyblok/common/ImageWithFallback"

import { getResizedSbImagepath } from "../ui/helpers/resize-sb-image"

function determineDirectlyAddableArticleId(productData: ReducedProductData | undefined) {
  // if (productData?.mastered) {
  if (!productData?.id.toUpperCase().startsWith("E")) {
    return productData?.id ?? null
  } else {
    return null
  }
}

interface ProductCardProps {
  productData: ReducedProductData | undefined
  /*  standardVariant: ReducedProductData | undefined */
  showDescription: boolean | undefined
  showEcoTaxFlag: boolean | undefined
  debugMode: boolean | undefined
  prices: ProductPriceContainerCtnrROV1 | undefined
  priceLoading?: boolean
  compact?: boolean
  hideAddToBasket: boolean
  isB2BCustomer: boolean
  generalDiscount?: number
  customerCategoryDiscount?: number
  trackingList?: string
  position?: number
  page?: number
  pageSize?: number
  priceRequestLink: string
  isoLocale: string
  view: string
  isRecommendation?: boolean
  mainProductId?: string
  searchTerm?: string
  campaignId?: string
  ffSearchParamsEncrypted?: string
  categoryPath?: string[]
  onClick?: React.MouseEventHandler<HTMLAnchorElement>
}

export default function ProductCard(props: ProductCardProps) {
  const t = useTranslations("storefront")
  /*  if(props.isRecommendation && !props.mainProductId) {
    console.error("mainProductId is required for recommendations")
  }else {
    console.info("mainProductId: " + props.mainProductId)
  }*/
  //console.log(props.isoLocale)
  const isListView = props.view == SEARCH_PARAMS_PAGE_VIEW_OPTIONS[1]
  //If a product is mastered, then we assume that it could be sold directly; otherwise the customer needs to choose a variant first on PDP
  // const hasVariants = !props.productData?.mastered

  const hasMulticolor = props.productData?.hasMultiColor

  const directlyAddableArticleId = determineDirectlyAddableArticleId(props.productData)
  const hasVariants = !directlyAddableArticleId
  //console.log("Test Card props.prices")
  //console.dir(props.prices, { depth: 7 })

  // Wichtig: originalProductId (Master-ID) durchreichen, damit isSinglePrice korrekt
  // anhand des Master-Produkts ermittelt wird. Hintergrund: Preise kommen ggf. von
  // cheapestVariant, wodurch sonst fälschlich ein Einzelpreis erkannt würde und das
  // "Ab"-Label verschwände.
  const cardPrices = determineCardPrices(
    props.isoLocale,
    props.isB2BCustomer,
    props.prices,
    props.productData?.ecoTax,
    undefined,
    props.productData?.id,
    createDiscountDisplayOptions(props.productData, props.generalDiscount, props.customerCategoryDiscount)
  )
  const { showPrice } = showProductPrice(props.productData, cardPrices)

  //UBW-650 - Always link to PDP first
  const cardButtonHref = props.productData?.link ?? ""
  // const cardButtonHref = showPrice ? props.productData?.link : props.priceRequestLink + props.productData?.id

  const { setIsLoading } = useUbStorefrontState()

  // to get rid of hydration problems with changing marker
  // the supressHydrationWarning attribute only works for the attribute in the link
  const [trackingMarker, setTrackingMarker] = useState<string | null>(null)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTrackingMarker(ulid())
  }, [])

  function handleProductClick(event: React.MouseEvent<HTMLAnchorElement>) {
    if (event.defaultPrevented) {
      props.onClick?.(event)
      return
    }

    if (props.categoryPath && props.productData?.id) {
      storeListingAttribution(
        buildCategoryListingAttribution(
          props.categoryPath,
          props.productData.id,
          props.position ? props.position - 1 : undefined
        )
      )
    } else {
      clearListingAttribution()
    }
    storeFactFinderSessionAttribution({
      productId: props.productData?.id,
      trackingList: props.trackingList,
      searchTerm: props.searchTerm,
      position: props.position,
    })
    props.onClick?.(event)
  }

  return (
    <>
      {props.productData ? (
        <Link
          prefetch={false}
          data-tracking-marker={trackingMarker}
          className={cn("@container/productcard size-full", { "sm:h-128": !props.compact && !isListView })}
          href={cardButtonHref ?? ""}
          suppressHydrationWarning
          onNavigate={() => {
            setIsLoading(true)
          }}
          onClick={handleProductClick}
        >
          {trackingMarker && (
            <>
              {props.isRecommendation && props.mainProductId ? (
                <FFRecommendationClickTrackingClient
                  trackingMarker={trackingMarker}
                  productData={props.productData}
                  mainProductId={props.mainProductId}
                />
              ) : (
                <FFProductClickTrackingClient
                  trackingMarker={trackingMarker}
                  productData={props.productData}
                  trackingList={props.trackingList ?? "direct"}
                  position={props.position ?? 1}
                  searchTerm={props.searchTerm}
                  page={props.page}
                  pageSize={props.pageSize}
                  campaignId={props.campaignId}
                />
              )}
            </>
          )}

          <ProductCardDiscount
            isoLocale={props.isoLocale}
            product={props.productData}
            prices={props.prices}
            isB2BCustomer={props.isB2BCustomer}
            _cardPrices={cardPrices}
            legacyPrices={true}
            className={cn("top-2 left-2", { "md:top-4 md:left-4": !props.compact })}
          />

          {/*inner main div*/}
          <div
            className={cn(
              `
                group/card grid size-full grid-cols-[minmax(0,2fr)_minmax(0,3fr)]
                grid-rows-[max-content_minmax(0,auto)_max-content_max-content] gap-2 p-2
              `,
              {
                "md:p-4": !props.compact,
                "@xl:grid-cols-[20%_minmax(0,auto)_minmax(min-content,20%)] @xl:grid-rows-[minmax(0,auto)_2.25rem]":
                  isListView,
              }
            )}
          >
            {/* image area */}
            <div
              className={cn("col-span-full flex flex-col items-center justify-center py-4 max-sm:py-0 sm:aspect-3/2", {
                "py-0": props.compact,
                "@xl:order-1 @xl:col-span-1 @xl:row-span-full @xl:aspect-[unset] @xl:py-0": isListView,
              })}
            >
              {props.productData.image && (
                <ImageWithFallback
                  unoptimized={true}
                  className={cn(
                    `
                      aspect-square max-h-64 w-9/12 object-contain transition duration-200 ease-in-out
                      motion-safe:group-hover/card:scale-105
                      max-sm:max-h-[15vh]
                    `,
                    {
                      "max-h-40": props.compact,
                      "@xl:w-3/5": isListView,
                    }
                  )}
                  alt={props.productData.title}
                  src={getResizedSbImagepath(props.productData.image)}
                  width={props.productData.image_width}
                  height={props.productData.image_height}
                />
              )}
            </div>
            {/* title, description */}
            <div
              className={cn("col-span-full", {
                "@xl:order-2 @xl:col-span-1": isListView,
              })}
            >
              <div className={cn("flex size-full flex-col gap-1 hyphens-auto", { "@xl:max-w-[80ch]": isListView })}>
                {isListView && (
                  <span className={"hidden text-xs font-light text-gray-600 @xl:inline"}>
                    {t("product.article_number")}: {determinePrefixedProductId(props.productData.id)}
                  </span>
                )}
                <span className={cn("line-clamp-2 text-sm leading-tight! font-bold md:text-base")}>
                  {props.productData.title}
                </span>
                {props.showDescription && (
                  <p
                    className={cn("line-clamp-1 text-xs md:text-sm", { "@xl:line-clamp-2": isListView })}
                    dangerouslySetInnerHTML={{
                      __html: props.productData.description ? props.productData.description : "",
                    }}
                  />
                )}
              </div>
            </div>
            {/* availability */}
            <div
              className={cn("flex min-w-0 flex-col justify-end", {
                "flex-row justify-start": props.compact,
                "@xl:order-4 @xl:flex-row @xl:items-end @xl:justify-start @xl:gap-1 @2xl:gap-4": isListView,
              })}
            >
              {hasMulticolor && (
                <div className="flex items-center justify-start gap-2">
                  <ImageWithFallback
                    unoptimized={true}
                    width={32}
                    height={32}
                    className={cn("aspect-square h-8 lg:h-6 xl:h-8", {
                      "@xl:aspect-[unset]! @xl:h-6!": isListView,
                      "h-4!": props.compact,
                    })}
                    alt={""}
                    src={"/img/multicolor.svg"}
                  />
                  {!props.compact && (
                    <p className={"text-xs/tight max-md:hidden lg:text-[0.65em] xl:text-xs"}>
                      {t("product.multiColor")}
                    </p>
                  )}
                </div>
              )}

              {showPrice && (
                <ProductCardDeliveryInfo
                  isoLocale={props.isoLocale}
                  productData={props.productData}
                  compact={props.compact}
                  isListView={isListView}
                />
              )}
            </div>
            {/* price */}
            <div className={cn("flex min-w-0 flex-col justify-end", { "@xl:order-3": isListView })}>
              <ProductCardPrice
                isoLocale={props.isoLocale}
                showEcoTaxFlag={props.showEcoTaxFlag}
                productData={props.productData}
                cardPrices={cardPrices}
                isB2BCustomer={props.isB2BCustomer}
                showPrice={showPrice}
                priceLoading={props.priceLoading}
                isListView={isListView}
              />
            </div>
            {/* buttons */}
            <div
              className={cn("col-span-full", {
                "@xl:order-5 @xl:col-span-1 @xl:flex @xl:flex-col @xl:justify-end": isListView,
              })}
            >
              {showPrice && hasVariants && (
                <Button size={"productCard"} className="w-full" variant={"default"}>
                  {t("product.go_to_variants")}
                </Button>
              )}

              {showPrice && !hasVariants && (
                <>
                  {isListView && (
                    <div className={"hidden w-full justify-end @xl:flex"}>
                      <div
                        className={"flex w-fit justify-between gap-x-2"}
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                        }}
                      >
                        <AddToBaskedWithQuantityInputClient
                          isoLocale={props.isoLocale}
                          isB2BCustomer={props.isB2BCustomer}
                          productSku={props.productData.id}
                          prices={undefined}
                          localState={true}
                          simulateProductClick={true}
                          article={props.productData}
                          compact={true}
                          trackingList={props.trackingList ?? "direct"}
                          position={props.position ?? 1}
                          searchTerm={props.searchTerm}
                          campaignId={props.campaignId}
                        />
                      </div>
                    </div>
                  )}
                  <div className={cn("flex w-full justify-between gap-x-2", { "@xl:hidden": isListView })}>
                    <div
                      className={cn("w-full md:w-3/4", {
                        "md:w-full": !directlyAddableArticleId || props.hideAddToBasket,
                      })}
                    >
                      <Button size={"productCard"} className="w-full" variant={"default"}>
                        {t("product.go_to_product")}
                      </Button>
                    </div>
                    <div
                      className={cn("block md:block", {
                        "w-0 md:hidden md:w-0": !directlyAddableArticleId || props.hideAddToBasket,
                      })}
                    >
                      {directlyAddableArticleId && !props.hideAddToBasket && (
                        <AddToBasketButtonClient
                          isoLocale={props.isoLocale}
                          isB2BCustomer={props.isB2BCustomer}
                          articleIdToAdd={directlyAddableArticleId}
                          productData={props.productData}
                          prices={props.prices}
                          quantity={props.productData?.stepOrderQuantity ?? 1}
                          trackingList={props.trackingList ?? "direct"}
                          position={props.position ?? 1}
                          searchTerm={props.searchTerm}
                          categoryPath={props.categoryPath}
                          campaignId={props.campaignId}
                          simulateProductClick={true}
                        />
                      )}
                    </div>
                  </div>
                </>
              )}

              {!showPrice && props.priceLoading && (
                <Button
                  size={"productCard"}
                  className="w-full animate-pulse bg-gray-200 text-transparent hover:bg-gray-200"
                  variant={"ghost"}
                  tabIndex={-1}
                >
                  {t("product.go_to_product")}
                </Button>
              )}

              {!showPrice && !props.priceLoading && (
                <Button size={"productCard"} className="w-full" variant={"default"}>
                  {t("product.request_price")}
                </Button>
              )}
            </div>
          </div>
        </Link>
      ) : (
        <div className="flex size-full items-center justify-center p-5">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img alt="Udobär Logo" src="/img/logo-de.png" />
        </div>
      )}
    </>
  )
}
